import React from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import { Text } from '@weave/design-system';
import { ReactComponent as StatusSuccessIcon } from 'images/status-success.svg';
import { ReactComponent as StatusErrorIcon } from 'images/status-error.svg';
import { ReactComponent as StatusAlertIcon } from 'images/status-warning.svg';

export type PayerListData = {
  type: 'payer-list';
  title: string;
  payers: {
    name: string;
    connected: boolean;
    mfaRequired: boolean;
    mfaActive: boolean;
  }[];
};

export const parsePayerListData = (value: string): PayerListData | string => {
  try {
    const parsed = JSON.parse(value);
    if (parsed?.type === 'payer-list' && Array.isArray(parsed.payers)) {
      return parsed as PayerListData;
    }
    return value;
  } catch {
    return value;
  }
};

export const buildPayerListNode = (data: PayerListData): React.ReactNode => (
  <div css={styles.container}>
    <Text size="medium" weight="bold" as="div">
      {data.title}
    </Text>
    <ul css={styles.list}>
      {data.payers.map((payer) => (
        <li key={payer.name} css={styles.item}>
          {!payer.connected ? (
            <StatusErrorIcon css={styles.icon} role="img" aria-label="Not connected" />
          ) : payer.mfaRequired && !payer.mfaActive ? (
            <StatusAlertIcon
              css={styles.icon}
              role="img"
              aria-label="Auto MFA setup is required"
            />
          ) : (
            <StatusSuccessIcon css={styles.icon} role="img" aria-label="Connected" />
          )}
          <Text size="medium" as="span">
            {payer.name}
            {payer.connected &&
              payer.mfaRequired &&
              !payer.mfaActive &&
              ' - [Auto MFA setup is required]'}
          </Text>
        </li>
      ))}
    </ul>
  </div>
);

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `,
  list: css`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
  `,
  item: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  icon: css`
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  `,
};
